<template>
  <div class="form-container">
    <v-card class="form-card" elevation="2">
      <!-- Cabeçalho -->
      <v-card-title class="form-header">
        <span class="form-title">{{ isEditMode ? 'Editar Elemento' : 'Cadastro de Elemento' }}</span>
        <v-checkbox
          v-model="formData.ativo"
          label="Ativo"
          color="blue"
          hide-details
          class="form-checkbox"
        />
      </v-card-title>

      <!-- Formulário -->
      <v-card-text class="form-content">
        <v-form ref="form" v-model="isFormValid">
          <!-- Linha 1: Exercício -->
          <v-row class="form-row">
            <v-col cols="12" sm="6" md="3" class="pa-1">
              <div class="form-label">Exercício</div>
              <v-select
                v-model="formData.exercicio"
                :items="exerciciosOptions"
                outlined
                dense
                :rules="[rules.required]"
                placeholder="Selecione um exercício"
                class="form-field"
                hide-details="auto"
                clearable
              />
            </v-col>
          </v-row>

          <!-- Linha 2: Código do Elemento e Descrição -->
          <v-row class="form-row">
            <v-col cols="12" sm="6" md="3" class="pa-1">
              <div class="form-label">Código do Elemento</div>
              <v-text-field
                v-model="formData.codigoElemento"
                type="text"
                outlined
                dense
                :rules="[rules.required, rules.numericOnly]"
                placeholder="Digite o código"
                class="form-field"
                hide-details="auto"
                @input="onNumericInput('codigoElemento', $event)"
                @keypress="onlyNumbers"
                @paste="onPaste"
              />
            </v-col>
            <v-col cols="12" sm="6" md="9" class="pa-1">
              <div class="form-label">Descrição</div>
              <v-text-field
                v-model="formData.descricao"
                outlined
                dense
                :rules="[rules.required]"
                placeholder="Digite a descrição"
                class="form-field"
                hide-details="auto"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <!-- Rodapé com Botões -->
      <v-card-actions class="form-actions">
        <v-btn
          @click="handleCancel"
          class="action-btn cancel-btn grey-btn"
        >
          Cancelar
        </v-btn>
        <v-btn
          @click="handleSave"
          color="primary"
          class="action-btn save-btn"
          :loading="isSaving"
        >
          <v-icon left>mdi-content-save</v-icon>
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Snackbar para mensagens -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="3000"
      top
    >
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showSnackbar = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { ExercicioService } from "../../services/ExercicioService";

export default {
  name: 'ElementoForm',
  props: {
    initialData: {
      type: Object,
      default: () => ({})
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    elementoId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      isFormValid: false,
      isSaving: false,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      formData: {
        exercicio: '',
        codigoElemento: '',
        descricao: '',
        ativo: true
      },
      rules: {
        required: v => !!v || 'Campo obrigatório',
        numericOnly: v => /^\d*$/.test(v) || 'Apenas números são permitidos'
      },
      exerciciosOptions: []
    }
  },
  created() {
    this.listarExerciciosSelect();
  },
  watch: {
    initialData: {
      handler(newData) {
        if (newData && Object.keys(newData).length > 0) {
          this.formData = {
            exercicio: newData.exercicio?.ano || '',
            codigoElemento: newData.codigo_elemento || '',
            descricao: newData.descricao || '',
            ativo: newData.ativo !== undefined ? newData.ativo : true
          };
        }
      },
      immediate: true
    }
  },
  methods: {
    async listarExerciciosSelect() {
      try {
        const response = await ExercicioService.select();
        if (response) {
          this.exerciciosOptions = response.data.map((el) => {
            return {
              text: el.ano,
              value: el.ano,
            };
          });
          this.exerciciosOptions?.unshift({
            text: "Selecione",
            value: "",
          });
        } else {
          console.error('Não foi possível obter os exercícios.');
        }
      } catch (error) {
        console.error('Ocorreu um erro ao listar os exercícios:', error);
      }
    },
    validateForm() {
      const isValid = this.$refs.form.validate();
      return isValid;
    },
    async handleSave() {
      if (!this.validateForm()) {
        this.showErrorMessage('Por favor, preencha todos os campos obrigatórios');
        return;
      }

      this.isSaving = true;
      
      try {
        // Emitir evento para o componente pai
        await this.$emit('save', this.formData);
        
      } catch (error) {
        this.showErrorMessage('Erro ao salvar o elemento. Tente novamente.');
      } finally {
        this.isSaving = false;
      }
    },
    handleCancel() {
      this.$emit('cancel');
    },
    clearForm() {
      if (!this.isEditMode) {
        this.formData = {
          exercicio: '',
          codigoElemento: '',
          descricao: '',
          ativo: true
        };
        this.$refs.form.resetValidation();
      }
    },
    showSuccessMessage(message) {
      this.snackbarColor = 'success';
      this.snackbarMessage = message;
      this.showSnackbar = true;
    },
    showErrorMessage(message) {
      this.snackbarColor = 'error';
      this.snackbarMessage = message;
      this.showSnackbar = true;
    },
    // Método para permitir apenas números na digitação
    onlyNumbers(event) {
      const charCode = event.which ? event.which : event.keyCode;
      
      // Bloquear explicitamente pontos e outros caracteres especiais
      if (charCode === 190 || charCode === 110 || charCode === 188 || charCode === 46) {
        event.preventDefault();
        return false;
      }
      
      // Permitir apenas números (48-57) e teclas especiais de navegação
      if (charCode < 48 || charCode > 57) {
        // Permitir apenas teclas especiais: backspace (8), tab (9), enter (13), setas (37-40)
        if (charCode !== 8 && charCode !== 9 && charCode !== 13 && 
            charCode !== 37 && charCode !== 38 && charCode !== 39 && charCode !== 40) {
          event.preventDefault();
          return false;
        }
      }
      
      return true;
    },
    // Método para interceptar colagem e filtrar apenas números
    onPaste(event) {
      event.preventDefault();
      const pastedText = (event.clipboardData || window.clipboardData).getData('text');
      const numericOnly = pastedText.replace(/[^0-9]/g, '');
      this.formData.codigoElemento = numericOnly;
    },
    // Método para filtrar entrada e manter apenas números
    onNumericInput(fieldName, value) {
      // Remove qualquer caractere que não seja número
      const numericValue = value.replace(/[^0-9]/g, '');
      this.formData[fieldName] = numericValue;
    }
  }
}
</script>

<style scoped>
@import '../../assets/styles/base-form.css';

/* Estilos específicos do ElementoForm, se necessário */
</style>
